import * as THREE from 'three';
import { C } from '../core/palette.js';
import { box, cyl, sphere } from '../core/meshkit.js';

// Toast Threads' stock — the one catalog shared by the shop (prices, labels,
// swatches) and by BreadBoi (the 3D mesh it wears). Each item's build() returns
// an Object3D sized to sit at the matching wardrobe anchor on the toast:
//   hat anchor ≈ body-local (0, 1.38, 0) — top of the head
//   tie anchor ≈ body-local (0, -.62, .36) — front, below the face
// Front of the character is +z, so brims/blades face +z. Add new drops by
// appending here; nothing else needs to change.

const GOLD = 0xF7C948;

export const COSMETICS = [
  // ---- hats ----
  {
    id: 'beret', slot: 'hat', name: 'Raspberry Beret', tag: '🎩', price: 8, swatch: '#E6537B',
    build(){
      const g = new THREE.Group();
      cyl(0.64, 0.68, 0.16, C.raspberry, 0, 0.08, 0.02, g, 20);
      cyl(0.05, 0.05, 0.09, C.crustDark, 0, 0.2, 0.02, g, 6);
      return g;
    }
  },
  {
    id: 'cap', slot: 'hat', name: 'Ballcap', tag: '🧢', price: 10, swatch: '#3D5FC0',
    build(){
      const g = new THREE.Group();
      const dome = sphere(0.56, C.cobalt, 0, 0.06, 0, g, 18); dome.scale.y = 0.62;
      box(0.72, 0.08, 0.5, C.cobalt, 0, 0.05, 0.52, g);     // brim, forward (+z)
      return g;
    }
  },
  {
    id: 'party', slot: 'hat', name: 'Party Hat', tag: '🥳', price: 12, swatch: '#7FBF6A',
    build(){
      const g = new THREE.Group();
      cyl(0, 0.44, 0.9, C.sprout, 0, 0.46, 0, g, 20);       // cone (rt=0 → point)
      sphere(0.11, C.raspberry, 0, 0.92, 0, g, 10);         // pom-pom
      return g;
    }
  },
  {
    id: 'chef', slot: 'hat', name: 'Chef Toque', tag: '👨‍🍳', price: 14, swatch: '#FFFFFF',
    build(){
      const g = new THREE.Group();
      cyl(0.44, 0.48, 0.3, C.white, 0, 0.15, 0, g, 20);     // band
      const puff = sphere(0.52, C.white, 0, 0.5, 0, g, 18); puff.scale.set(1, 0.85, 1);
      return g;
    }
  },
  {
    id: 'tophat', slot: 'hat', name: 'Top Hat', tag: '🎩', price: 22, swatch: '#6E4526',
    build(){
      const g = new THREE.Group();
      cyl(0.78, 0.82, 0.06, C.crustDark, 0, 0.03, 0, g, 24);  // brim
      cyl(0.5, 0.52, 0.72, C.crustDark, 0, 0.42, 0, g, 24);   // crown
      cyl(0.53, 0.53, 0.12, C.raspberry, 0, 0.2, 0, g, 24);   // band
      return g;
    }
  },
  {
    id: 'crown', slot: 'hat', name: 'Crumb Crown', tag: '👑', price: 30, swatch: '#F7C948',
    build(){
      const g = new THREE.Group();
      cyl(0.52, 0.52, 0.3, GOLD, 0, 0.15, 0, g, 24);          // band ring
      for (let i = 0; i < 6; i++){
        const a = (i / 6) * Math.PI * 2;
        const x = Math.cos(a) * 0.44, z = Math.sin(a) * 0.44;
        cyl(0, 0.1, 0.34, GOLD, x, 0.42, z, g, 8);            // point
        sphere(0.06, C.raspberry, x, 0.6, z, g, 8);           // jewel
      }
      return g;
    }
  },
  // ---- ties ----
  {
    id: 'bowtie', slot: 'tie', name: 'Bow Tie', tag: '🎀', price: 6, swatch: '#E6537B',
    build(){
      const g = new THREE.Group();
      box(0.16, 0.18, 0.08, C.raspberry, 0, 0, 0, g);         // knot
      const l = box(0.24, 0.28, 0.06, C.raspberry, -0.2, 0, 0, g); l.rotation.z = 0.35;
      const r = box(0.24, 0.28, 0.06, C.raspberry, 0.2, 0, 0, g);  r.rotation.z = -0.35;
      return g;
    }
  },
  {
    id: 'necktie', slot: 'tie', name: 'Necktie', tag: '👔', price: 9, swatch: '#3D5FC0',
    build(){
      const g = new THREE.Group();
      box(0.17, 0.15, 0.06, C.cobalt, 0, 0.03, 0, g);         // knot
      box(0.2, 0.58, 0.05, C.cobalt, 0, -0.32, 0, g);         // blade hanging down
      return g;
    }
  },
  {
    id: 'bolo', slot: 'tie', name: 'Bolo Tie', tag: '🤠', price: 12, swatch: '#8C6239',
    build(){
      const g = new THREE.Group();
      const l = box(0.03, 0.44, 0.03, C.crustDark, -0.09, -0.2, 0, g); l.rotation.z = 0.12;
      const r = box(0.03, 0.44, 0.03, C.crustDark, 0.09, -0.2, 0, g);  r.rotation.z = -0.12;
      sphere(0.11, GOLD, 0, -0.02, 0.03, g, 12);              // clasp
      return g;
    }
  },
  // ---- space program reward (granted by finishing the Fix-the-Spaceship chain) ----
  {
    id: 'space-helmet', slot: 'hat', name: 'Astronaut Helmet', tag: '🧑‍🚀', price: 0, swatch: '#DDEEFF', reward: true,
    build(){
      const g = new THREE.Group();
      const dome = sphere(0.62, 0xEAF4FF, 0, 0.16, 0, g, 20);            // clear bubble dome
      dome.material = new THREE.MeshLambertMaterial({ color: 0xBFE0FF, transparent: true, opacity: 0.55 });
      cyl(0.6, 0.66, 0.16, C.white, 0, -0.2, 0, g, 20);                  // collar ring
      box(0.5, 0.14, 0.04, 0xF7C948, 0, 0.16, 0.6, g);                   // gold visor stripe
      return g;
    }
  },
  {
    id: 'oxygen-pack', slot: 'tie', name: 'Oxygen Pack', tag: '🫧', price: 0, swatch: '#C9D6E0', reward: true,
    build(){
      const g = new THREE.Group();
      box(0.34, 0.4, 0.14, 0xC9D6E0, 0, -0.18, -0.02, g);                // chest control panel
      sphere(0.05, 0x7FBF6A, -0.08, -0.1, 0.06, g, 8);                   // green light
      sphere(0.05, 0xE6537B, 0.08, -0.1, 0.06, g, 8);                    // red light
      cyl(0.03, 0.03, 0.3, C.white, -0.14, -0.2, -0.02, g, 6);           // air hose
      return g;
    }
  }
];

export function findCosmetic(id){ return COSMETICS.find(c => c.id === id) || null; }
